import { CoreFieldMaps, AdditionalField, Condition, ParseResult, Transform, TransformMap } from "./types";

/** Converts a path array like `["device", "gps", "lat"]` to jq syntax: `.device.gps.lat` */
export function jqPath(pathSegments: string[]): string {
    return pathSegments.map(seg =>
        /^\d+$/.test(seg) ? `[${seg}]`       // array index: [0], [1], ...
        : /^\w+$/.test(seg) ? `.${seg}`       // simple key: .name
        : `."${seg}"`                          // special chars: ."frm-payload"
    ).join("");
}

/** Safely traverses a nested object following `pathSegments`. Returns `undefined` if any key is missing. */
export function valAt(obj: any, pathSegments: string[]): any {
    let current = obj;
    for (const key of pathSegments) {
        if (current == null) return undefined;
        current = current[key];
    }
    return current;
}

/** Sets `value` at a dotted path in `obj`, creating intermediate objects as needed. */
function setAtDotted(obj: any, dottedKey: string, value: any): void {
    const segments = dottedKey.split(".");
    let current = obj;
    for (let i = 0; i < segments.length - 1; i++) {
        const seg = segments[i];
        if (current[seg] == null || typeof current[seg] !== "object") current[seg] = {};
        current = current[seg];
    }
    current[segments[segments.length - 1]] = value;
}

/**
 * Replaces the contents of every string literal with spaces (quotes kept, length preserved),
 * so regex-based structural checks can't be fooled by text inside user data
 * (e.g. a condition value containing `as $x` or `word: .path`).
 */
function maskStrings(s: string): string {
    let out = "";
    for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (ch === '"') {
            out += '"';
            i++;
            let contentLength = 0;
            while (i < s.length && s[i] !== '"') {
                if (s[i] === "\\") { i++; contentLength++; }
                contentLength++;
                i++;
            }
            out += " ".repeat(contentLength) + (i < s.length ? '"' : "");
        } else {
            out += ch;
        }
    }
    return out;
}

/**
 * Splits a jq path string into segments, handling quoted keys like `."frm-payload"`.
 * Input: `uplink_message.locations."frm-payload".latitude`
 * Output: `["uplink_message", "locations", "frm-payload", "latitude"]`
 */
function splitJqPath(pathStr: string): string[] {
    const segments: string[] = [];
    let cursor = 0;
    while (cursor < pathStr.length) {
        if (pathStr[cursor] === ".") { cursor++; continue; }
        if (pathStr[cursor] === "[") {
            // Bracket notation: [0], ["key"]
            const closeBracket = pathStr.indexOf("]", cursor + 1);
            if (closeBracket === -1) break;
            let inner = pathStr.slice(cursor + 1, closeBracket);
            // Strip quotes if present: ["key"] → key
            if (inner.startsWith('"') && inner.endsWith('"')) inner = inner.slice(1, -1);
            segments.push(inner);
            cursor = closeBracket + 1;
        } else if (pathStr[cursor] === '"') {
            const closeQuote = pathStr.indexOf('"', cursor + 1);
            if (closeQuote === -1) break;
            segments.push(pathStr.slice(cursor + 1, closeQuote));
            cursor = closeQuote + 1;
        } else {
            let end = cursor;
            while (end < pathStr.length && pathStr[end] !== "." && pathStr[end] !== '"' && pathStr[end] !== "[") end++;
            segments.push(pathStr.slice(cursor, end));
            cursor = end;
        }
    }
    return segments;
}

/** Recursively flattens a nested object/array into an array of `{ path, value }` entries for every key (including intermediate objects and array items). */
export function flatPaths(obj: any, prefix: string[] = []): { path: string[]; value: any }[] {
    const result: { path: string[]; value: any }[] = [];
    if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
            const fullPath = [...prefix, String(index)];
            result.push({ path: fullPath, value: item });
            if (item && typeof item === "object") {
                result.push(...flatPaths(item, fullPath));
            }
        });
    } else if (obj && typeof obj === "object") {
        for (const [key, value] of Object.entries(obj)) {
            const fullPath = [...prefix, key];
            result.push({ path: fullPath, value });
            if (value && typeof value === "object") {
                result.push(...flatPaths(value, fullPath));
            }
        }
    }
    return result;
}

/** Returns the jq key name for the "additional" bag depending on object type. */
export function addlKey(objectType: string): string {
    return objectType === "ev" ? "event_details" : "additional";
}

// ── Per-field transforms ──
//
// Each transform emits a *canonical* jq form so the parser can recognize it on
// round-trip. `applyTransforms` wraps a base path expression; all forms except
// `coalesce` are pipe stages operating on `.`. `coalesce` wraps the base path
// itself (`(BASE // .fallback)`) because the fallback is a path from the root,
// not from the piped value. Every form deliberately avoids `as $var`, `def`, and
// pipe-assignment so it never trips hasStructuralComplexity.

// Strict JSON-number grammar. Number() alone accepts tokens jq rejects ("0x1F", "Infinity"),
// which would make the emitted filter fail jq compilation.
const JSON_NUMBER_RE = /^-?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?$/;

/** Emits a jq literal for a user-entered scalar: JSON numbers/booleans/null stay bare, everything else is quoted. */
function jqLiteral(raw: string): string {
    const s = (raw ?? "").trim();
    if (s === "true" || s === "false" || s === "null") return s;
    if (JSON_NUMBER_RE.test(s)) return s;
    return JSON.stringify(raw ?? "");
}

/** The typed JS value that the literal emitted by jqLiteral(raw) evaluates to — keeps previews type-accurate. */
function jqLiteralValue(raw: string): any {
    const s = (raw ?? "").trim();
    if (s === "true") return true;
    if (s === "false") return false;
    if (s === "null") return null;
    if (JSON_NUMBER_RE.test(s)) return Number(s);
    return raw ?? "";
}

/** Parses a jq literal token back to the raw string the user entered (inverse of jqLiteral). */
function parseJqLiteral(raw: string): string {
    const s = raw.trim();
    if (s.startsWith('"')) {
        try { return JSON.parse(s); } catch { return s.replace(/^"|"$/g, ""); }
    }
    return s;
}

/** Normalizes a user-typed source path (".cached.lat", " cached.lat ") to the internal dotted form. */
function normalizePathArg(arg: string): string {
    return (arg ?? "").trim().replace(/^\.+/, "");
}

/** A divide argument is usable only when it parses to a finite, non-zero JSON number. */
function parseDivisor(arg?: string): number | null {
    const s = (arg ?? "").trim();
    if (!JSON_NUMBER_RE.test(s) && isNaN(Number(s))) return null;
    const d = Number(s);
    return isFinite(d) && d !== 0 ? d : null;
}

/** Emits the value-map object literal `{"from": to, ...}` for value_map transforms. */
function valueMapObject(pairs: { from: string; to: string }[]): string {
    const entries = pairs
        .filter(p => p.from !== "")
        .map(p => `${JSON.stringify(p.from)}: ${jqLiteral(p.to)}`);
    return `{${entries.join(", ")}}`;
}

/**
 * Emits a single pipe-stage for a transform (all kinds except coalesce, which wraps the base).
 * Stages coerce first (tonumber/tostring) so real-world payloads that send numbers as strings
 * (or vice versa) don't hard-error in jq — mirroring the JS preview's Number()/String() coercions.
 * Returns null for stages that cannot emit (unusable divide arg); applyTransformsJs skips the same ones.
 */
function transformStage(t: Transform): string | null {
    switch (t.kind) {
        case "tonumber": return "tonumber";
        case "tostring": return "tostring";
        case "epoch_iso_s": return "(tonumber | todate)";
        case "epoch_iso_ms": return "(tonumber / 1000 | floor | todate)";
        case "divide": {
            const d = parseDivisor(t.arg);
            if (d === null) return null;
            // Keep the user's spelling when it's already a valid jq number ("1e6");
            // normalize only tokens jq would reject ("0x1F" → "31").
            const raw = (t.arg ?? "").trim();
            return `(tonumber / ${JSON_NUMBER_RE.test(raw) ? raw : d})`;
        }
        case "split": return `(tostring | split(${JSON.stringify(t.arg ?? "")}))`;
        case "default": return `(. // ${jqLiteral(t.arg ?? "")})`;
        // [tostring] index: numeric source values would error on a plain [.] object index.
        case "value_map": return `(${valueMapObject(t.pairs ?? [])}[tostring] // .)`;
        default: return null;
    }
}

/**
 * Canonical jq for a field with NO mapped source path, only transforms (e.g. a hardcoded
 * event_type). A single `default` collapses to the plain literal — byte-identical to the
 * legacy literal-input output, so old saved configs and new ones converge. Anything else
 * pipes from an explicit `null` base, which the parser recognizes on round-trip.
 */
export function unmappedFieldExpr(transforms: Transform[]): string {
    if (transforms.length === 1 && transforms[0].kind === "default") {
        return jqLiteral(transforms[0].arg ?? "");
    }
    return applyTransforms("null", transforms);
}

/** Wraps a base jq path expression with the given transforms, producing canonical jq. */
export function applyTransforms(baseExpr: string, transforms?: Transform[]): string {
    if (!transforms || transforms.length === 0) return baseExpr;

    let expr = baseExpr;
    // Coalesce wraps the base path (fallbacks are root paths, not piped values); chains in order.
    const fallbacks = transforms
        .filter(t => t.kind === "coalesce" && normalizePathArg(t.arg ?? ""))
        .map(t => jqPath(normalizePathArg(t.arg!).split(".")));
    if (fallbacks.length) expr = `(${expr} // ${fallbacks.join(" // ")})`;

    const stages = transforms
        .filter(t => t.kind !== "coalesce")
        .map(transformStage)
        .filter(Boolean) as string[];
    if (stages.length) expr = `(${expr} | ${stages.join(" | ")})`;

    return expr;
}

/** jq's `//` falls back on null AND false — mirror that exactly in previews. */
function jqAbsent(v: any): boolean {
    return v === undefined || v === null || v === false;
}

/**
 * jq `tonumber` semantics: numbers pass through, numeric strings parse, everything
 * else errors — modeled as NaN so the type validator flags it (JS `Number(null)`
 * would silently give 0 where jq errors).
 */
function jqToNumber(v: any): number {
    if (typeof v === "number") return v;
    // Only strict JSON-number strings: Number() alone accepts tokens jq rejects
    // ("0x1F", " 5 "), which would make the preview pass where the filter errors.
    if (typeof v === "string" && JSON_NUMBER_RE.test(v)) return Number(v);
    return NaN;
}

/** jq `tostring` semantics: strings pass through, everything else becomes its JSON text
 *  (JS `String({})` would give "[object Object]" where jq produces `{"a":1}`). */
function jqToString(v: any): string {
    return typeof v === "string" ? v : (JSON.stringify(v) ?? "null");
}

/** Applies transforms to a concrete JS value for the live preview (mirrors transformStage semantics). */
export function applyTransformsJs(value: any, transforms: Transform[] | undefined, rootInput: any): any {
    if (!transforms || transforms.length === 0) return value;

    let v = value;
    // Coalesce chain first, in pill order — matches the emitted `(BASE // .f1 // .f2)`.
    for (const t of transforms) {
        if (t.kind !== "coalesce") continue;
        const fallbackPath = normalizePathArg(t.arg ?? "");
        if (fallbackPath && jqAbsent(v)) v = valAt(rootInput, fallbackPath.split("."));
    }

    for (const t of transforms) {
        switch (t.kind) {
            case "tonumber": v = jqToNumber(v); break;
            case "tostring": v = jqToString(v); break;
            case "epoch_iso_s": v = epochToIso(jqToNumber(v)); break;
            case "epoch_iso_ms": v = epochToIso(Math.floor(jqToNumber(v) / 1000)); break;
            case "divide": {
                const d = parseDivisor(t.arg);
                if (d !== null) v = jqToNumber(v) / d;
                break;
            }
            case "split": v = jqToString(v).split(t.arg ?? ""); break;
            case "default": if (jqAbsent(v)) v = jqLiteralValue(t.arg ?? ""); break;
            case "value_map": {
                // Mirror the emitted object literal exactly: empty `from` keys are dropped
                // by valueMapObject, and duplicate keys are last-wins in jq object literals.
                const key = jqToString(v);
                const hit = [...(t.pairs ?? [])].reverse().find(p => p.from !== "" && p.from === key);
                // jq `map[tostring] // .` keeps the original when the mapped value is null/false.
                if (hit) {
                    const mapped = jqLiteralValue(hit.to);
                    if (!jqAbsent(mapped)) v = mapped;
                }
                break;
            }
            default: break;
        }
    }
    return v;
}

/** Converts an epoch-seconds value to an ISO8601 string (jq `todate` equivalent).
 *  Out-of-range epochs (Date only covers ±8.64e15 ms — e.g. nanosecond timestamps) would
 *  make toISOString throw mid-render; jq `todate` errors on them too, so model as NaN. */
function epochToIso(epochSeconds: number): any {
    if (!isFinite(epochSeconds) || Math.abs(epochSeconds) > 8.64e12) return NaN;
    return new Date(epochSeconds * 1000).toISOString().replace(/\.\d{3}Z$/, "Z");
}

/** Keeps coalesce pills ahead of pipe-stage pills (stable) so the visible pill order
 *  always matches execution order — applyTransforms hoists coalesce into the base
 *  expression, and the parser reads fallbacks back before pipe stages. */
export function normalizeTransforms(transforms: Transform[]): Transform[] {
    return [
        ...transforms.filter(t => t.kind === "coalesce"),
        ...transforms.filter(t => t.kind !== "coalesce"),
    ];
}

/** Preview text for a transformed value. NaN is the "jq would error here" marker —
 *  JSON.stringify would print it as "null", contradicting the NaN validation message. */
export function formatPreviewText(v: any): string | undefined {
    if (v === undefined) return undefined;
    return typeof v === "number" && isNaN(v) ? "NaN" : JSON.stringify(v);
}

// ── Field-source resolution (shared by genJq and evalMapping) ──

type ResolvedField = { mapping: string[] | null; chain?: Transform[] };

/** jq expression for a resolved field — a mapped path wrapped in its transforms, or a
 *  null-base chain for a field carried entirely by transforms. */
function resolvedFieldJq(f: ResolvedField): string {
    return f.mapping ? applyTransforms(jqPath(f.mapping), f.chain) : unmappedFieldExpr(f.chain ?? []);
}

/**
 * Decides which mapping and/or transform chain carries every output field — the single
 * rule shared by genJq (the emitted filter) and evalMapping (the live preview), so the
 * two can never silently diverge. A chain whose emission collapses to bare `null` (e.g.
 * a coalesce pill with no path typed yet) is not usable — emitting `field: null` would
 * persist, then reload as a hardcoded `default "null"` pill.
 */
function resolveFieldSources(coreFieldMaps: CoreFieldMaps, transforms: TransformMap, additionalFields: AdditionalField[]) {
    const chainUsable = (chain?: Transform[]) => !!chain?.length && unmappedFieldExpr(chain) !== "null";

    const fields: ({ key: string } & ResolvedField)[] = [];
    for (const [key, mapping] of Object.entries(coreFieldMaps)) {
        if (!mapping) continue;
        if (key === "location.lat" || key === "location.lon") continue;
        fields.push({ key, mapping, chain: transforms[key] });
    }
    // Unmapped fields carried entirely by transforms (default/coalesce chains — e.g. a
    // hardcoded event_type, or a subject_type coalesced from a fallback path).
    for (const [key, chain] of Object.entries(transforms)) {
        if (coreFieldMaps[key] || !chainUsable(chain)) continue;
        if (key === "location.lat" || key === "location.lon") continue;
        fields.push({ key, mapping: null, chain });
    }

    const axis = (key: "location.lat" | "location.lon"): ResolvedField | null => {
        const mapping = coreFieldMaps[key];
        if (mapping) return { mapping, chain: transforms[key] };
        return chainUsable(transforms[key]) ? { mapping: null, chain: transforms[key] } : null;
    };
    const lat = axis("location.lat");
    const lon = axis("location.lon");

    // A named additional field is carried by a mapped path OR a usable transform chain
    // (a path-less field is carried entirely by its transforms — same rule as core fields).
    const additional = additionalFields
        .filter(f => f.name && (f.path || chainUsable(f.transforms)))
        .map(f => ({ name: f.name, mapping: f.path ? f.path.split(".") : null, chain: f.transforms }));

    return { fields, location: lat || lon ? { lat, lon } : null, additional };
}

// ── jq generation ──

function formatConditionValue(value: string): string | number {
    return isNaN(Number(value)) ? JSON.stringify(value) : Number(value);
}

function conditionToJq(condition: Condition): string | null {
    // Use jqPath so numeric segments are bracketed (`.gws[0].time`), matching
    // the body of the generated mapping. Internal storage stays dotted.
    const fieldPath = jqPath(condition.field.split("."));
    const formattedValue = formatConditionValue(condition.value);
    const numericValue = Number(condition.value);

    switch (condition.op) {
        case "not_null": return `${fieldPath} != null`;
        case "is_null": return `${fieldPath} == null`;
        case "equals": return `${fieldPath} == ${formattedValue}`;
        case "not_equals": return `${fieldPath} != ${formattedValue}`;
        case "gt": return `${fieldPath} > ${numericValue}`;
        case "lt": return `${fieldPath} < ${numericValue}`;
        case "contains": return `(${fieldPath} | tostring | contains(${JSON.stringify(condition.value || "")}))`;
        default: return null;
    }
}

/**
 * Generates a complete jq filter string from the builder state.
 * Produces `{ field: .path, ... }` or `if (conditions) then { ... } else empty end`.
 */
export function genJq(
    conditions: Condition[],
    coreFieldMaps: CoreFieldMaps,
    additionalFields: AdditionalField[],
    objectType = "obv",
    transforms: TransformMap = {},
): string {
    const conditionClauses = conditions
        .filter(c => c.field)
        .map(conditionToJq)
        .filter(Boolean) as string[];

    const outputLines: string[] = [];
    const src = resolveFieldSources(coreFieldMaps, transforms, additionalFields);

    // Core fields — mapped, or carried entirely by transforms (location is nested below).
    for (const f of src.fields) {
        outputLines.push(`  ${f.key}: ${resolvedFieldJq(f)}`);
    }

    // Location object (combine lat/lon into nested structure)
    if (src.location) {
        const lat = src.location.lat ? resolvedFieldJq(src.location.lat) : "null";
        const lon = src.location.lon ? resolvedFieldJq(src.location.lon) : "null";
        outputLines.push(`  location: { lat: ${lat}, lon: ${lon} }`);
    }

    // Additional / event_details fields
    const additionalLines = src.additional.map(f => `    ${JSON.stringify(f.name)}: ${resolvedFieldJq(f)}`);
    if (additionalLines.length) {
        outputLines.push(`  "${addlKey(objectType)}": {\n${additionalLines.join(",\n")}\n  }`);
    }

    const body = "{\n" + outputLines.join(",\n") + "\n}";
    return conditionClauses.length
        ? `if ( ${conditionClauses.join(" and ")} ) then ${body} else empty end`
        : body;
}

// ── Evaluation (preview) ──

export type EvalResult = { data: any; failedConditions: string[] };

/**
 * Preview value for a mapped field. When the source path is missing, coalesce/default
 * transforms can still rescue the value (that's their whole point) — otherwise show the
 * unmapped placeholder.
 */
function previewValue(val: any, transforms: Transform[] | undefined, input: any, placeholder: string): any {
    const rescuable = transforms?.some(t => t.kind === "coalesce" || t.kind === "default");
    if (val === undefined && !rescuable) return placeholder;
    const transformed = applyTransformsJs(val, transforms, input);
    return transformed !== undefined ? transformed : placeholder;
}

function evaluateCondition(condition: Condition, inputValue: any): boolean {
    const coercedValue = isNaN(Number(condition.value)) ? condition.value : Number(condition.value);

    switch (condition.op) {
        case "not_null": return inputValue != null;
        case "is_null": return inputValue == null;
        case "equals": return inputValue === coercedValue;
        case "not_equals": return inputValue !== coercedValue;
        case "gt": return inputValue > Number(condition.value);
        case "lt": return inputValue < Number(condition.value);
        case "contains": return String(inputValue).includes(condition.value || "");
        default: return true;
    }
}

function formatFailedCondition(condition: Condition): string {
    const valueSuffix = condition.value ? ` ${condition.value}` : "";
    return `${jqPath(condition.field.split("."))} ${condition.op}${valueSuffix}`;
}

/**
 * Evaluates the builder mapping against a sample input object.
 * Returns the transformed output, or a list of failed conditions if the record would be filtered out.
 */
export function evalMapping(
    input: any,
    coreFieldMaps: CoreFieldMaps,
    additionalFields: AdditionalField[],
    conditions: Condition[],
    objectType = "obv",
    complexFields: Record<string, string> = {},
    transforms: TransformMap = {},
): EvalResult {
    // Check conditions
    const failedConditions: string[] = [];
    for (const condition of conditions) {
        if (!condition.field) continue;
        const fieldValue = valAt(input, condition.field.split("."));
        if (!evaluateCondition(condition, fieldValue)) {
            failedConditions.push(formatFailedCondition(condition));
        }
    }
    if (failedConditions.length > 0) return { data: null, failedConditions };

    // Build output object
    const result: any = {};
    const src = resolveFieldSources(coreFieldMaps, transforms, additionalFields);
    // Preview value for a resolved field — the JS mirror of resolvedFieldJq.
    const resolvedFieldValue = (f: { mapping: string[] | null; chain?: Transform[] }): any =>
        f.mapping ? previewValue(valAt(input, f.mapping), f.chain, input, `<${jqPath(f.mapping)}>`)
            : applyTransformsJs(null, f.chain ?? [], input);

    // Complex fields — include as expression placeholder so validation sees them as mapped.
    // Dotted keys (e.g. "location.lat") must be nested so the validator's getNestedValue resolves them.
    for (const [key, expr] of Object.entries(complexFields)) {
        if (key.startsWith("addl:")) continue; // handled below in additional bag
        setAtDotted(result, key, `<${expr}>`);
    }

    for (const f of src.fields) result[f.key] = resolvedFieldValue(f);

    if (src.location) {
        // Merge with any complex-field placeholders already set via setAtDotted — a mapped lat
        // must not clobber a complex lon (or vice versa).
        const location: any = (result.location && typeof result.location === "object") ? result.location : {};
        if (src.location.lat) location.lat = resolvedFieldValue(src.location.lat);
        else if (location.lat === undefined) location.lat = null;
        if (src.location.lon) location.lon = resolvedFieldValue(src.location.lon);
        else if (location.lon === undefined) location.lon = null;
        result.location = location;
    }

    const additionalBag: any = {};
    for (const f of src.additional) additionalBag[f.name] = resolvedFieldValue(f);
    // Include complex additional fields as placeholders
    for (const [key, expr] of Object.entries(complexFields)) {
        if (!key.startsWith("addl:")) continue;
        additionalBag[key.slice(5)] = `<${expr}>`;
    }
    if (Object.keys(additionalBag).length) {
        result[addlKey(objectType)] = additionalBag;
    }

    return { data: result, failedConditions: [] };
}

// ── Parser (jq string → builder state) ──

// Matches a jq path expression — covers `.foo.bar`, `.foo[0].bar`, `."weird-key"`.
// Accepts the legacy dot-index form (`.foo.0.bar`) for backward compatibility.
// Matches up to (but not including) whitespace / `|` / `)` delimiters.
//
// NOTE: the character class intentionally over-matches — it accepts `=`, `<`, `>`
// too, so in the comparison regex (`^${PATH_GROUP}\s*(==|!=|>|<)\s*(.+)$`) a
// no-space input like `.foo==5` is first swallowed whole by this greedy group.
// The match only succeeds because the regex engine backtracks the path group far
// enough to let the operator alternation claim `==`. This is harmless for our own
// output (genJq always emits spaces around operators), but keep it in mind before
// narrowing or widening this class.
const PATH_GROUP = String.raw`(\.[^\s|)]+)`;
/** Converts a captured jq path back to the internal dotted form (e.g. `gws[0].time` → `gws.0.time`). */
function jqPathToDotted(jqPathStr: string): string {
    return splitJqPath(jqPathStr).join(".");
}

// ── Transform expression parsing (canonical forms only) ──
//
// Recognizes exactly the jq shapes applyTransforms emits, so newly-created
// transforms round-trip back into pills. Anything else returns null and stays a
// complex field (Code mode) — we never guess at hand-written idioms.

/**
 * True if `s` is a bare jq path the builder can faithfully round-trip: `.a.b`, `.a[0].b`,
 * `."weird-key"`, or the legacy dot-index form `.a.0.b`. Rejects iterate (`.a[]`), dynamic
 * indexing (`.a[.b]`), and anything whose regenerated form would change semantics —
 * misclassifying those as paths silently corrupts the filter on re-save.
 */
function isStrictPath(s: string): boolean {
    const t = s.trim();
    // Structural pre-check runs on a form with string-literal contents collapsed, so a
    // quoted key may contain spaces or operators (`."cached lat"` — which jqPath emits)
    // without tripping the delimiter/`//` rejections.
    const collapsed = maskStrings(t).replace(/" *"/g, '""');
    if (!/^\.[^\s|()]+$/.test(collapsed) || collapsed.includes("//")) return false;
    const segments = splitJqPath(t);
    if (segments.length === 0 || segments.some(seg => seg === "")) return false;
    // Regenerating must reproduce the original (canonical bracket form or legacy dotted form).
    return jqPath(segments) === t || `.${segments.join(".")}` === t;
}

/** If `s` is fully wrapped in one balanced paren pair, returns the inner content; otherwise null. */
function stripOuterParens(s: string): string | null {
    const t = s.trim();
    if (!t.startsWith("(") || !t.endsWith(")")) return null;
    let depth = 0;
    for (let i = 0; i < t.length; i++) {
        const ch = t[i];
        if (ch === '"') { i++; while (i < t.length && t[i] !== '"') { if (t[i] === "\\") i++; i++; } continue; }
        if (ch === "(") depth++;
        else if (ch === ")") { depth--; if (depth === 0 && i !== t.length - 1) return null; }
    }
    return t.slice(1, -1);
}

/** Splits `s` on a top-level separator token, respecting nested brackets and string literals. */
function splitTopLevel(s: string, sep: string): string[] {
    const parts: string[] = [];
    let depth = 0;
    let last = 0;
    for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (ch === '"') { i++; while (i < s.length && s[i] !== '"') { if (s[i] === "\\") i++; i++; } continue; }
        if (ch === "(" || ch === "[" || ch === "{") depth++;
        else if (ch === ")" || ch === "]" || ch === "}") depth--;
        else if (depth === 0 && s.startsWith(sep, i)) {
            parts.push(s.slice(last, i));
            i += sep.length - 1;
            last = i + 1;
        }
    }
    parts.push(s.slice(last));
    return parts;
}

// Regex fragments for canonical-form recognition.
const JQ_STRING_SRC = String.raw`"(?:[^"\\]|\\.)*"`;
const JQ_NUMBER_SRC = String.raw`-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?`;
const JQ_LITERAL_SRC = `${JQ_STRING_SRC}|true|false|null|${JQ_NUMBER_SRC}`;

/** Recognizes a single canonical pipe-stage (all transform kinds except coalesce). */
function matchTransformStage(stage: string): Transform | null {
    const s = stage.trim();
    if (s === "tonumber") return { kind: "tonumber" };
    if (s === "tostring") return { kind: "tostring" };
    if (/^\(\s*tonumber\s*\|\s*todate\s*\)$/.test(s)) return { kind: "epoch_iso_s" };
    if (/^\(\s*tonumber\s*\/\s*1000\s*\|\s*floor\s*\|\s*todate\s*\)$/.test(s)) return { kind: "epoch_iso_ms" };

    let m: RegExpMatchArray | null;
    if ((m = s.match(new RegExp(String.raw`^\(\s*tonumber\s*\/\s*(${JQ_NUMBER_SRC})\s*\)$`)))) {
        return { kind: "divide", arg: m[1] };
    }
    if ((m = s.match(new RegExp(String.raw`^\(\s*tostring\s*\|\s*split\(\s*(${JQ_STRING_SRC})\s*\)\s*\)$`)))) {
        try { return { kind: "split", arg: JSON.parse(m[1]) }; } catch { return null; }
    }
    if ((m = s.match(/^\(\s*(\{[\s\S]*\})\s*\[\s*tostring\s*\]\s*\/\/\s*\.\s*\)$/))) {
        try {
            // The emitted object literal is valid JSON (keys and string values via JSON.stringify).
            const obj = JSON.parse(m[1]);
            if (obj === null || typeof obj !== "object" || Array.isArray(obj)) return null;
            // An empty-string key is a hand-written idiom (maps blank input) that
            // valueMapObject would silently drop on re-save — keep it in Code mode.
            if (Object.prototype.hasOwnProperty.call(obj, "")) return null;
            const pairs = Object.entries(obj).map(([from, to]) => ({ from, to: String(to) }));
            return { kind: "value_map", pairs };
        } catch { return null; }
    }
    // Default accepts ONLY jq literals. A path/expression fallback like `(. // .cached_speed)`
    // must NOT parse as a default transform — regenerating it would rewrite the path into a
    // string constant, silently corrupting the filter.
    if ((m = s.match(new RegExp(String.raw`^\(\s*\.\s*\/\/\s*(${JQ_LITERAL_SRC})\s*\)$`)))) {
        return { kind: "default", arg: parseJqLiteral(m[1]) };
    }
    return null;
}

/**
 * Parses a canonical transform expression into a base path + transform list.
 * A `null` base (`(null // .f | ...)`, emitted by unmappedFieldExpr) parses to
 * `path: null` — an unmapped field carried entirely by its transforms.
 * Returns null for anything that isn't a shape the builder produces.
 */
export function parseTransformExpression(expr: string): { path: string[] | null; transforms: Transform[] } | null {
    const inner = stripOuterParens(expr);
    if (inner === null) return null;

    const pipeParts = splitTopLevel(inner, "|");
    let base = pipeParts[0].trim();
    const stageParts = pipeParts.slice(1);

    const transforms: Transform[] = [];

    // Base may itself be a coalesce wrap: `(.x // .y // .z)` or bare `.x // .y`.
    const unwrappedBase = base.startsWith("(") ? stripOuterParens(base) : null;
    if (unwrappedBase !== null) base = unwrappedBase.trim();

    // Every `//`-separated part must be a strict path; the first is the mapping
    // (or the literal `null` for an unmapped field), the rest become chained
    // coalesce transforms in order.
    const coalesceParts = splitTopLevel(base, "//").map(p => p.trim());
    const mainPart = coalesceParts[0];
    if (mainPart !== "null" && !isStrictPath(mainPart)) return null;
    if (!coalesceParts.slice(1).every(isStrictPath)) return null;
    for (const fallback of coalesceParts.slice(1)) {
        transforms.push({ kind: "coalesce", arg: jqPathToDotted(fallback) });
    }

    for (const stage of stageParts) {
        const t = matchTransformStage(stage);
        if (!t) return null;
        transforms.push(t);
    }

    // A bare `(null)` with no transforms is not a builder shape.
    if (mainPart === "null" && transforms.length === 0) return null;

    return { path: mainPart === "null" ? null : splitJqPath(mainPart), transforms };
}

/** Parses the condition wrapper. Returns recognized conditions plus any clauses it could not parse. */
function parseConditions(conditionString: string): { conds: Condition[]; unrecognized: string[] } {
    const conditions: Condition[] = [];
    const unrecognized: string[] = [];

    // Quote/paren-aware split: a condition VALUE containing " and " (e.g. "on and off")
    // must not shatter the clause.
    for (const part of splitTopLevel(conditionString, " and ")) {
        const trimmed = part.trim();
        if (!trimmed) continue;

        const containsMatch = trimmed.match(new RegExp(`^\\(${PATH_GROUP}\\s*\\|\\s*tostring\\s*\\|\\s*contains\\("([^"]*)"\\)\\)$`));
        if (containsMatch) {
            conditions.push({ field: jqPathToDotted(containsMatch[1]), op: "contains", value: containsMatch[2] });
            continue;
        }

        const notNullMatch = trimmed.match(new RegExp(`^${PATH_GROUP}\\s*!=\\s*null$`));
        if (notNullMatch) {
            conditions.push({ field: jqPathToDotted(notNullMatch[1]), op: "not_null", value: "" });
            continue;
        }

        const isNullMatch = trimmed.match(new RegExp(`^${PATH_GROUP}\\s*==\\s*null$`));
        if (isNullMatch) {
            conditions.push({ field: jqPathToDotted(isNullMatch[1]), op: "is_null", value: "" });
            continue;
        }

        const comparisonMatch = trimmed.match(new RegExp(`^${PATH_GROUP}\\s*(==|!=|>|<)\\s*(.+)$`));
        if (comparisonMatch) {
            const operatorMap: Record<string, string> = { "==": "equals", "!=": "not_equals", ">": "gt", "<": "lt" };
            let value = comparisonMatch[3].trim();
            const stringMatch = value.match(/^"([^"]*)"$/);
            if (stringMatch) value = stringMatch[1];
            conditions.push({ field: jqPathToDotted(comparisonMatch[1]), op: operatorMap[comparisonMatch[2]], value });
            continue;
        }

        // Unrecognized clause — record it so the caller can mark the filter non-round-trippable
        // rather than silently dropping the condition (which would loosen the filter on re-save).
        unrecognized.push(trimmed);
    }

    return { conds: conditions, unrecognized };
}

// ── Body tokenizer + field-expression classifier ──
//
// The output object is tokenized ONCE into top-level `key: <expr>` pairs (string- and
// nesting-aware), and each expression goes through a single classification ladder.
// This replaces the previous whole-body regex scans, which matched keys and paths inside
// nested objects and string literals — corrupting value-map/default arguments and
// injecting spurious fields on regeneration.

type TopLevelField = { key: string; expr: string };

/** Extracts a balanced expression starting at `str[from]`; returns the expression and the index after it. */
function extractExpressionAt(str: string, from: number): { expr: string; end: number } {
    let depth = 0;
    let i = from;
    for (; i < str.length; i++) {
        const ch = str[i];
        if (ch === '"') {
            i++;
            while (i < str.length && str[i] !== '"') { if (str[i] === "\\") i++; i++; }
            continue;
        }
        if (ch === "(" || ch === "{" || ch === "[") depth++;
        else if (ch === ")" || ch === "}" || ch === "]") {
            if (depth === 0) break; // closing brace of parent object
            depth--;
        } else if (ch === "," && depth === 0) break;
    }
    return { expr: str.slice(from, i).trim(), end: i };
}

/** Tokenizes a `{ ... }` object body into its top-level key/expression pairs.
 *  `clean` is false when any chunk could not be parsed as `key: expr` — callers must
 *  treat the filter as non-round-trippable rather than regenerate from a partial read. */
function extractTopLevelFields(objBody: string): { fields: TopLevelField[]; clean: boolean } {
    const trimmed = objBody.trim();
    if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return { fields: [], clean: false };
    const inner = trimmed.slice(1, -1);

    const fields: TopLevelField[] = [];
    let clean = true;
    let i = 0;
    while (i < inner.length) {
        while (i < inner.length && /[\s,]/.test(inner[i])) i++;
        if (i >= inner.length) break;

        let key: string | null = null;
        if (inner[i] === '"') {
            let closeQuote = i + 1;
            while (closeQuote < inner.length && inner[closeQuote] !== '"') {
                if (inner[closeQuote] === "\\") closeQuote++;
                closeQuote++;
            }
            if (closeQuote < inner.length) {
                try { key = JSON.parse(inner.slice(i, closeQuote + 1)); } catch { key = inner.slice(i + 1, closeQuote); }
                i = closeQuote + 1;
            }
        } else {
            const identMatch = inner.slice(i).match(/^[A-Za-z_]\w*/);
            if (identMatch) { key = identMatch[0]; i += identMatch[0].length; }
        }
        if (key === null) {
            clean = false;
            i = extractExpressionAt(inner, i).end + 1;
            continue;
        }

        while (i < inner.length && /\s/.test(inner[i])) i++;
        if (inner[i] !== ":") {
            clean = false;
            i = extractExpressionAt(inner, i).end + 1;
            continue;
        }
        i++;

        const { expr, end } = extractExpressionAt(inner, i);
        i = end;
        if (expr) fields.push({ key, expr });
        else clean = false;
    }
    return { fields, clean };
}

type FieldExprClass =
    | { kind: "path"; path: string[] }
    | { kind: "literal"; value: string }
    | { kind: "transform"; path: string[] | null; transforms: Transform[] }
    | { kind: "object"; body: string }
    | { kind: "complex" };

const JQ_LITERAL_ONLY_RE = new RegExp(`^(?:${JQ_LITERAL_SRC})$`);

/** Classifies a field expression through a single ladder — the one place that decides
 *  whether an expression is builder-representable. */
function classifyFieldExpression(expr: string): FieldExprClass {
    const t = expr.trim();
    if (t.startsWith("{") && t.endsWith("}")) return { kind: "object", body: t };
    // Bare literals (strings, numbers, booleans) — unmappedFieldExpr emits these for a
    // single-default field, and legacy literal-input configs are plain strings.
    if (JQ_LITERAL_ONLY_RE.test(t)) return { kind: "literal", value: parseJqLiteral(t) };
    if (isStrictPath(t)) return { kind: "path", path: splitJqPath(t) };
    const transform = parseTransformExpression(t);
    if (transform) return { kind: "transform", path: transform.path, transforms: transform.transforms };
    return { kind: "complex" };
}

/** Checks if the jq string has structural complexity beyond what the builder supports.
 *  Runs on string-masked text so user data (a condition value containing `as $x`) can't
 *  trip the checks. Pipe preambles/trailers are detected separately via the output-object
 *  offsets in parseJqToBuilderState. */
function hasStructuralComplexity(jq: string): boolean {
    const masked = maskStrings(jq);
    if (/\bas\s+\$\w+/.test(masked)) return true;       // variable bindings
    if (/\bdef\s+\w+/.test(masked)) return true;        // function definitions
    if (/\|\s*\.\w+\s*=[^=]/.test(masked)) return true; // pipe-assignment (.x = expr)
    return false;
}

/** Finds the opening `{` that matches the last `}` in the string, respecting nesting. */
/** Finds the outermost `{ ... }` block by scanning backwards from the last `}` to its matching `{`. */
function findOutputObjectRange(jqBody: string): { start: number; end: number } | null {
    const closingBraceIndex = jqBody.lastIndexOf("}");
    if (closingBraceIndex === -1) return null;

    let braceDepth = 0;
    for (let cursor = closingBraceIndex; cursor >= 0; cursor--) {
        const char = jqBody[cursor];
        if (char === "}") braceDepth++;
        else if (char === "{") {
            braceDepth--;
            if (braceDepth === 0) {
                return { start: cursor, end: closingBraceIndex + 1 };
            }
        }
    }
    return null;
}

/**
 * Parses an existing jq filter string back into builder state.
 * Returns `null` only for empty input. For complex/unparseable jq, returns a result
 * with `isFullyParseable: false` and populated `complexFields`.
 */
export function parseJqToBuilderState(jq: string): ParseResult | null {
    try {
        const trimmed = jq.trim();
        if (!trimmed) return null;

        const structurallyComplex = hasStructuralComplexity(trimmed);

        // Separate condition wrapper from body
        let conditionString = "";
        let body = trimmed;

        // Pattern 1: if (conds) then { ... } else empty end
        const ifMatch = trimmed.match(/^if\s*\(\s*([\s\S]+?)\s*\)\s*then\s*([\s\S]+?)\s*else\s*empty\s*end\s*$/);
        if (ifMatch) {
            conditionString = ifMatch[1];
            body = ifMatch[2].trim();
        }

        // Pattern 2: select(conds) | { ... }
        if (!conditionString && /^\s*select\s*\(/.test(body)) {
            let depth = 1;
            const selectStart = body.indexOf("select(") + 7;
            let closeParen = -1;
            for (let i = selectStart; i < body.length; i++) {
                if (body[i] === "(") depth++;
                else if (body[i] === ")") {
                    depth--;
                    if (depth === 0) { closeParen = i; break; }
                }
            }
            if (closeParen !== -1) {
                const afterSelect = body.slice(closeParen + 1).trim();
                if (afterSelect.startsWith("|")) {
                    conditionString = body.slice(selectStart, closeParen);
                    body = afterSelect.slice(1).trim();
                }
            }
        }

        // Find the outermost output object `{ ... }` by matching the last `}` with its `{`
        const range = findOutputObjectRange(body);
        if (!range) {
            return { maps: {}, conds: [], addl: [], complexFields: {}, transforms: {}, isFullyParseable: false, rawJq: trimmed };
        }
        const outputBody = body.slice(range.start, range.end);
        // A pipe preamble before the object, or trailing content after it, means the filter
        // does more than the builder can represent.
        const hasBodyPreamble = range.start > 0 || body.slice(range.end).trim().length > 0;

        const parsedConds = conditionString ? parseConditions(conditionString) : { conds: [], unrecognized: [] };
        const conds = parsedConds.conds;

        const maps: CoreFieldMaps = {};
        const transforms: TransformMap = {};
        const addl: AdditionalField[] = [];
        const complexFields: Record<string, string> = {};

        const { fields: topFields, clean: bodyClean } = extractTopLevelFields(outputBody);
        let nestedClean = true;

        for (const { key, expr } of topFields) {
            // Nested location object → classify each axis independently.
            if (key === "location") {
                const cls = classifyFieldExpression(expr);
                if (cls.kind !== "object") { complexFields[key] = expr; continue; }
                const { fields: locFields, clean: locClean } = extractTopLevelFields(cls.body);
                if (!locClean) { complexFields[key] = expr; nestedClean = false; continue; }
                for (const { key: axis, expr: axisExpr } of locFields) {
                    const target = `location.${axis}`;
                    if (axis !== "lat" && axis !== "lon") { complexFields[target] = axisExpr; continue; }
                    if (axisExpr === "null") continue; // genJq emits null for an unmapped axis
                    const axisCls = classifyFieldExpression(axisExpr);
                    if (axisCls.kind === "path") maps[target] = axisCls.path;
                    else if (axisCls.kind === "transform") {
                        if (axisCls.path) maps[target] = axisCls.path;
                        if (axisCls.transforms.length) transforms[target] = axisCls.transforms;
                    } else if (axisCls.kind === "literal") {
                        transforms[target] = [{ kind: "default", arg: axisCls.value }];
                    } else complexFields[target] = axisExpr;
                }
                continue;
            }

            // Additional / event_details bag → each entry is a user-named field.
            if (key === "additional" || key === "event_details") {
                const cls = classifyFieldExpression(expr);
                if (cls.kind !== "object") { complexFields[key] = expr; continue; }
                const { fields: addlFields, clean: addlClean } = extractTopLevelFields(cls.body);
                if (!addlClean) { complexFields[key] = expr; nestedClean = false; continue; }
                for (const { key: name, expr: fieldExpr } of addlFields) {
                    const fieldCls = classifyFieldExpression(fieldExpr);
                    if (fieldCls.kind === "path") addl.push({ name, path: fieldCls.path.join(".") });
                    else if (fieldCls.kind === "transform") {
                        // A path-less (null-base) chain is a path-less additional field.
                        addl.push({ name, path: fieldCls.path ? fieldCls.path.join(".") : "", transforms: fieldCls.transforms.length ? fieldCls.transforms : undefined });
                    } else if (fieldCls.kind === "literal") {
                        // A bare literal collapses to a single default pill on a path-less field.
                        addl.push({ name, path: "", transforms: [{ kind: "default", arg: fieldCls.value }] });
                    } else complexFields[`addl:${name}`] = fieldExpr;
                }
                continue;
            }

            const cls = classifyFieldExpression(expr);
            // A bare literal (legacy literal-input output, or unmappedFieldExpr's collapsed
            // single-default) becomes an unmapped field with a "Default if empty" pill.
            if (cls.kind === "literal") transforms[key] = [{ kind: "default", arg: cls.value }];
            else if (cls.kind === "path") maps[key] = cls.path;
            else if (cls.kind === "transform") {
                if (cls.path) maps[key] = cls.path;
                if (cls.transforms.length) transforms[key] = cls.transforms;
            } else complexFields[key] = expr;
        }

        const isFullyParseable = !structurallyComplex
            && bodyClean
            && nestedClean
            && Object.keys(complexFields).length === 0
            && parsedConds.unrecognized.length === 0
            && !hasBodyPreamble;

        return { maps, conds, addl, complexFields, transforms, isFullyParseable, rawJq: trimmed };
    } catch {
        return { maps: {}, conds: [], addl: [], complexFields: {}, transforms: {}, isFullyParseable: false, rawJq: jq.trim() };
    }
}
